import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  DEFAULT_ORG_TIMEZONE,
  formatOrgDate,
  type OrgDateFormat,
  type FormatOrgDateOptions,
} from '@vipso/shared';
import type { RootState } from '../store';

export type DateFormat = OrgDateFormat;

export type FormatDateOptions = FormatOrgDateOptions;

/**
 * Org-aware date formatter.
 *
 * Uses `org.timezone` from the auth store (falls back to `Asia/Kolkata`).
 * Device/browser timezone is never used.
 *
 * @example
 * const { format, timeZone } = useDate();
 * format(order.createdAt);                 // default 'date' → 29 Jul 2026
 * format(order.createdAt, 'datetime');     // 29 Jul 2026, 4:30 pm
 * format(order.createdAt, 'short');        // 29/07/2026
 * format(order.createdAt, { format: 'long' });
 */
export function useDate() {
  const orgTimezone = useSelector((state: RootState) => {
    const tz = state.auth.org?.timezone;
    return typeof tz === 'string' && tz.trim() ? tz.trim() : null;
  });

  const timeZone = orgTimezone || DEFAULT_ORG_TIMEZONE;

  const format = useCallback(
    (
      value: string | Date | number | null | undefined,
      formatOrOptions: DateFormat | FormatDateOptions = 'date',
    ) => formatOrgDate(value, timeZone, formatOrOptions),
    [timeZone],
  );

  return useMemo(() => ({ format, timeZone }), [format, timeZone]);
}
